"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import type { SavedDeckEntry } from "@/lib/local-store";

export type PrepCard = {
  id: string;
  name: string;
  quantity: number;
  imageUrl?: string | null;
};

type DragSource = "main" | "sideboard" | "in" | "out";

type DragPayload = {
  cardId: string;
  name: string;
  imageUrl?: string | null;
  source: DragSource;
  max: number;
};

type DropZone = "in" | "out" | "main" | "sideboard";

function selectionQty(entries: SavedDeckEntry[], cardId: string) {
  return entries.find((e) => e.cardId === cardId)?.quantity ?? 0;
}

function CardFace({
  entry,
  badge,
  badgeClass,
  dimmed,
  children,
}: {
  entry: PrepCard;
  badge?: string;
  badgeClass?: string;
  dimmed?: boolean;
  children?: ReactNode;
}) {
  return (
    <div
      className={`relative select-none overflow-hidden rounded-lg border border-white/10 bg-black/50 md:touch-none ${
        dimmed ? "opacity-40" : ""
      }`}
    >
      {entry.imageUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={entry.imageUrl}
          alt={entry.name}
          title={entry.name}
          draggable={false}
          className="aspect-[5/7] w-full object-cover"
        />
      ) : (
        <div className="flex aspect-[5/7] items-center justify-center px-1 text-center text-[9px] leading-tight text-gray-400">
          {entry.name}
        </div>
      )}
      {badge ? (
        <span
          className={`absolute bottom-0.5 right-0.5 rounded px-1 py-0.5 text-[9px] font-bold tabular-nums shadow ${
            badgeClass ?? "bg-black/80 text-white"
          }`}
        >
          {badge}
        </span>
      ) : null}
      {children}
    </div>
  );
}

function DropPanel({
  zone,
  title,
  accent,
  emptyLabel,
  active,
  children,
}: {
  zone: DropZone;
  title: string;
  accent: string;
  emptyLabel: string;
  active: boolean;
  children: ReactNode;
}) {
  return (
    <div
      data-drop-zone={zone}
      className={`flex min-h-0 flex-1 flex-col rounded-xl border p-2 transition ${
        active ? "border-white/50 bg-white/10 ring-2 ring-white/30" : accent
      }`}
    >
      <p className="mb-1.5 shrink-0 text-[10px] font-bold uppercase tracking-wide text-gray-300">
        {title}
      </p>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {children ?? <p className="px-1 text-[11px] text-gray-600">{emptyLabel}</p>}
      </div>
    </div>
  );
}

function qtyBadge(quantity: number) {
  return quantity > 1 ? `x${quantity}` : undefined;
}

function bestFitLayout(count: number, width: number, height: number, gap = 4) {
  if (count <= 0 || width <= 0 || height <= 0) {
    return { cols: 1, cardW: Math.max(40, width) };
  }
  let bestCols = 1;
  let bestCardW = 0;
  const maxCols = Math.min(count, 14);
  for (let cols = 1; cols <= maxCols; cols++) {
    const rows = Math.ceil(count / cols);
    const cellW = (width - gap * (cols - 1)) / cols;
    const cellH = (height - gap * (rows - 1)) / rows;
    const cardW = Math.min(cellW, cellH * (5 / 7));
    if (cardW > bestCardW) {
      bestCardW = cardW;
      bestCols = cols;
    }
  }
  return { cols: bestCols, cardW: Math.max(32, bestCardW) };
}

function FitCardGrid({
  count,
  mobileCols = 4,
  children,
}: {
  count: number;
  mobileCols?: number;
  children: (cardStyle: CSSProperties) => ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const [layout, setLayout] = useState({ cols: mobileCols, cardW: 0 });

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const apply = () => setIsDesktop(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!isDesktop) {
      setLayout({ cols: mobileCols, cardW: 0 });
      return;
    }
    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      setLayout(bestFitLayout(count, width, height));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [count, isDesktop, mobileCols]);

  const cardStyle: CSSProperties =
    isDesktop && layout.cardW > 0
      ? { width: layout.cardW, maxWidth: "100%" }
      : { width: "100%" };

  return (
    <div
      ref={ref}
      className="grid min-h-0 flex-1 content-start justify-items-center gap-1 overflow-y-auto md:overflow-hidden"
      style={{
        gridTemplateColumns: `repeat(${layout.cols}, minmax(0, 1fr))`,
      }}
    >
      {children(cardStyle)}
    </div>
  );
}

export default function PrepSideEditor({
  mainEntries,
  sideboardEntries,
  sideIn,
  sideOut,
  onBumpIn,
  onBumpOut,
}: {
  mainEntries: PrepCard[];
  sideboardEntries: PrepCard[];
  sideIn: SavedDeckEntry[];
  sideOut: SavedDeckEntry[];
  onBumpIn: (cardId: string, delta: number, max: number) => void;
  onBumpOut: (cardId: string, delta: number, max: number) => void;
}) {
  const [drag, setDrag] = useState<{
    payload: DragPayload;
    x: number;
    y: number;
    moved: boolean;
  } | null>(null);
  const [hoverZone, setHoverZone] = useState<DropZone | null>(null);
  const [canDrag, setCanDrag] = useState(false);
  const dragRef = useRef(drag);
  const skipClickRef = useRef(false);
  const originRef = useRef({ x: 0, y: 0 });
  dragRef.current = drag;

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const apply = () => {
      const desktop = mq.matches;
      setCanDrag(desktop);
      if (!desktop) {
        setDrag(null);
        setHoverZone(null);
      }
    };
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  const resolveZone = useCallback((x: number, y: number): DropZone | null => {
    const el = document.elementFromPoint(x, y);
    const zone = el?.closest?.("[data-drop-zone]")?.getAttribute("data-drop-zone");
    if (zone === "in" || zone === "out" || zone === "main" || zone === "sideboard") return zone;
    return null;
  }, []);

  const applyDrop = useCallback(
    (payload: DragPayload, zone: DropZone | null) => {
      if (!zone) return;

      if (payload.source === "main") {
        if (zone === "out") onBumpOut(payload.cardId, 1, payload.max);
        return;
      }
      if (payload.source === "sideboard") {
        if (zone === "in") onBumpIn(payload.cardId, 1, payload.max);
        return;
      }
      if (payload.source === "out") {
        if (zone === "main" || zone === "out") onBumpOut(payload.cardId, -1, payload.max);
        return;
      }
      if (payload.source === "in") {
        if (zone === "sideboard" || zone === "in") onBumpIn(payload.cardId, -1, payload.max);
      }
    },
    [onBumpIn, onBumpOut],
  );

  const dragging = !!drag;

  useEffect(() => {
    if (!dragging) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";

    const onMove = (e: PointerEvent) => {
      e.preventDefault();
      const moved =
        Math.abs(e.clientX - originRef.current.x) > 8 ||
        Math.abs(e.clientY - originRef.current.y) > 8;
      setDrag((prevDrag) =>
        prevDrag
          ? { ...prevDrag, x: e.clientX, y: e.clientY, moved: prevDrag.moved || moved }
          : null,
      );
      setHoverZone(resolveZone(e.clientX, e.clientY));
    };
    const onUp = (e: PointerEvent) => {
      const current = dragRef.current;
      if (current?.moved) {
        skipClickRef.current = true;
        applyDrop(current.payload, resolveZone(e.clientX, e.clientY));
      }
      setDrag(null);
      setHoverZone(null);
    };

    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      document.body.style.overflow = prev;
      document.body.style.touchAction = "";
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [dragging, applyDrop, resolveZone]);

  function startDrag(e: ReactPointerEvent, payload: DragPayload) {
    if (!canDrag) return;
    if (e.button !== 0) return;
    e.preventDefault();
    skipClickRef.current = false;
    originRef.current = { x: e.clientX, y: e.clientY };
    setDrag({ payload, x: e.clientX, y: e.clientY, moved: false });
    setHoverZone(resolveZone(e.clientX, e.clientY));
  }

  function handleTap(action: () => void) {
    if (skipClickRef.current) {
      skipClickRef.current = false;
      return;
    }
    action();
  }

  const inCards: PrepCard[] = sideIn.map((e) => {
    const base =
      sideboardEntries.find((c) => c.id === e.cardId) ||
      mainEntries.find((c) => c.id === e.cardId);
    return {
      id: e.cardId,
      name: base?.name ?? e.cardId,
      quantity: e.quantity,
      imageUrl: base?.imageUrl,
    };
  });

  const outCards: PrepCard[] = sideOut.map((e) => {
    const base =
      mainEntries.find((c) => c.id === e.cardId) ||
      sideboardEntries.find((c) => c.id === e.cardId);
    return {
      id: e.cardId,
      name: base?.name ?? e.cardId,
      quantity: e.quantity,
      imageUrl: base?.imageUrl,
    };
  });

  const mainCardCount = useMemo(
    () => mainEntries.reduce((sum, entry) => sum + entry.quantity, 0),
    [mainEntries],
  );
  const sideCardCount = useMemo(
    () => sideboardEntries.reduce((sum, entry) => sum + entry.quantity, 0),
    [sideboardEntries],
  );

  return (
    <div className="relative flex min-h-0 flex-1 flex-col gap-1.5">
      {/* Mobile: Deck → In/Out → Sideboard | Desktop: 3 colonnes */}
      <div className="grid min-h-0 flex-1 grid-rows-[minmax(0,1.15fr)_minmax(110px,0.9fr)_minmax(0,1.15fr)] gap-1.5 md:grid-cols-[minmax(0,1.15fr)_minmax(180px,0.7fr)_minmax(0,1.15fr)] md:grid-rows-1">
        {/* 1. Deck */}
        <div
          data-drop-zone="main"
          className={`flex min-h-0 flex-col rounded-xl border p-1.5 md:p-2 ${
            hoverZone === "main" && drag?.payload.source === "out"
              ? "border-rose-400/60 bg-rose-500/10"
              : "border-white/10 bg-black/25"
          }`}
        >
          <p className="mb-1 shrink-0 text-[10px] font-bold uppercase tracking-wide text-gray-400">
            Deck · {canDrag ? "→ Out" : "clic = Out"} ({mainCardCount})
          </p>
          <FitCardGrid count={mainEntries.length} mobileCols={4}>
            {(cardStyle) =>
              mainEntries.map((entry) => {
                const selected = selectionQty(sideOut, entry.id);
                const remaining = entry.quantity - selected;
                const canOut = remaining > 0;
                const isFullyOut = remaining <= 0;
                const qtyLabel = qtyBadge(remaining > 0 ? remaining : entry.quantity);
                return (
                  <button
                    key={entry.id}
                    type="button"
                    style={cardStyle}
                    disabled={!canOut && selected === 0}
                    onPointerDown={(e) =>
                      startDrag(e, {
                        cardId: entry.id,
                        name: entry.name,
                        imageUrl: entry.imageUrl,
                        source: isFullyOut ? "out" : "main",
                        max: entry.quantity,
                      })
                    }
                    onClick={() => {
                      handleTap(() => {
                        if (canOut) onBumpOut(entry.id, 1, entry.quantity);
                        else if (selected > 0) onBumpOut(entry.id, -1, entry.quantity);
                      });
                    }}
                    className={`min-w-0 ${canDrag ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"} disabled:cursor-default`}
                  >
                    <CardFace
                      entry={entry}
                      badge={isFullyOut ? "OUT" : selected > 0 ? `OUT ${selected}` : qtyLabel}
                      badgeClass={
                        selected > 0 ? "bg-rose-600/90 text-white" : "bg-black/80 text-white"
                      }
                      dimmed={isFullyOut}
                    />
                  </button>
                );
              })
            }
          </FitCardGrid>
        </div>

        {/* 2. Out / In — mobile: Out gauche / In droite · PC: Out haut / In bas */}
        <div className="grid min-h-0 grid-cols-2 gap-1.5 md:grid-cols-1 md:grid-rows-2">
          <DropPanel
            zone="out"
            title={`Out (${outCards.reduce((s, c) => s + c.quantity, 0)})`}
            accent="border-rose-500/25 bg-rose-500/5"
            emptyLabel="Deck → ici"
            active={hoverZone === "out" && drag?.payload.source === "main"}
          >
            {outCards.length > 0 ? (
              <div className="grid grid-cols-3 gap-1.5 md:grid-cols-3">
                {outCards.map((entry) => {
                  const max =
                    mainEntries.find((c) => c.id === entry.id)?.quantity ?? entry.quantity;
                  return (
                    <button
                      key={entry.id}
                      type="button"
                      onPointerDown={(e) =>
                        startDrag(e, {
                          cardId: entry.id,
                          name: entry.name,
                          imageUrl: entry.imageUrl,
                          source: "out",
                          max,
                        })
                      }
                      onClick={() => handleTap(() => onBumpOut(entry.id, -1, max))}
                      className={canDrag ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"}
                    >
                      <CardFace
                        entry={entry}
                        badge={`x${entry.quantity}`}
                        badgeClass="bg-rose-600/90 text-white"
                      />
                    </button>
                  );
                })}
              </div>
            ) : null}
          </DropPanel>

          <DropPanel
            zone="in"
            title={`In (${inCards.reduce((s, c) => s + c.quantity, 0)})`}
            accent="border-emerald-500/25 bg-emerald-500/5"
            emptyLabel="Side → ici"
            active={hoverZone === "in" && drag?.payload.source === "sideboard"}
          >
            {inCards.length > 0 ? (
              <div className="grid grid-cols-3 gap-1.5 md:grid-cols-3">
                {inCards.map((entry) => {
                  const max =
                    sideboardEntries.find((c) => c.id === entry.id)?.quantity ?? entry.quantity;
                  return (
                    <button
                      key={entry.id}
                      type="button"
                      onPointerDown={(e) =>
                        startDrag(e, {
                          cardId: entry.id,
                          name: entry.name,
                          imageUrl: entry.imageUrl,
                          source: "in",
                          max,
                        })
                      }
                      onClick={() => handleTap(() => onBumpIn(entry.id, -1, max))}
                      className={canDrag ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"}
                    >
                      <CardFace
                        entry={entry}
                        badge={`x${entry.quantity}`}
                        badgeClass="bg-emerald-600/90 text-white"
                      />
                    </button>
                  );
                })}
              </div>
            ) : null}
          </DropPanel>
        </div>

        {/* 3. Sideboard */}
        <div
          data-drop-zone="sideboard"
          className={`flex min-h-0 flex-col rounded-xl border p-1.5 md:p-2 ${
            hoverZone === "sideboard" && drag?.payload.source === "in"
              ? "border-emerald-400/60 bg-emerald-500/10"
              : "border-white/10 bg-black/25"
          }`}
        >
          <p className="mb-1 shrink-0 text-[10px] font-bold uppercase tracking-wide text-gray-400">
            Sideboard · {canDrag ? "→ In" : "clic = In"} ({sideCardCount})
          </p>
          {sideboardEntries.length === 0 ? (
            <p className="text-[11px] text-gray-600">Pas de sideboard sur ce deck.</p>
          ) : (
            <FitCardGrid count={sideboardEntries.length} mobileCols={4}>
              {(cardStyle) =>
                sideboardEntries.map((entry) => {
                  const selected = selectionQty(sideIn, entry.id);
                  const remaining = entry.quantity - selected;
                  const canIn = remaining > 0;
                  const isFullyIn = remaining <= 0;
                  const qtyLabel = qtyBadge(remaining > 0 ? remaining : entry.quantity);
                  return (
                    <button
                      key={entry.id}
                      type="button"
                      style={cardStyle}
                      disabled={!canIn && selected === 0}
                      onPointerDown={(e) =>
                        startDrag(e, {
                          cardId: entry.id,
                          name: entry.name,
                          imageUrl: entry.imageUrl,
                          source: isFullyIn ? "in" : "sideboard",
                          max: entry.quantity,
                        })
                      }
                      onClick={() => {
                        handleTap(() => {
                          if (canIn) onBumpIn(entry.id, 1, entry.quantity);
                          else if (selected > 0) onBumpIn(entry.id, -1, entry.quantity);
                        });
                      }}
                      className={`min-w-0 ${canDrag ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"} disabled:cursor-default`}
                    >
                      <CardFace
                        entry={entry}
                        badge={isFullyIn ? "IN" : selected > 0 ? `IN ${selected}` : qtyLabel}
                        badgeClass={
                          selected > 0
                            ? "bg-emerald-600/90 text-white"
                            : "bg-black/80 text-white"
                        }
                        dimmed={isFullyIn}
                      />
                    </button>
                  );
                })
              }
            </FitCardGrid>
          )}
        </div>
      </div>

      {drag ? (
        <div
          className="pointer-events-none fixed z-[80] w-14 -translate-x-1/2 -translate-y-1/2 opacity-90 shadow-2xl sm:w-16"
          style={{ left: drag.x, top: drag.y }}
        >
          <CardFace
            entry={{
              id: drag.payload.cardId,
              name: drag.payload.name,
              quantity: 1,
              imageUrl: drag.payload.imageUrl,
            }}
            badge="+1"
            badgeClass={
              drag.payload.source === "main" || drag.payload.source === "out"
                ? "bg-rose-600 text-white"
                : "bg-emerald-600 text-white"
            }
          />
        </div>
      ) : null}
    </div>
  );
}
